import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithReset, selectAtom } from 'jotai/utils';

import { userAtom } from '@/_atoms/auth';
import { hasSkipGroups } from '@/_helpers/navHelpers';

import type { User } from '@/types/auth';
import type { Demographic } from '@/types/respondent';
import type { Element } from '@/types/survey';

/** A map of item IDs to Demographic objects indicating the respondent's reply to each field. */
export const demographicMapAtom = atomWithReset<Map<string, Demographic[]>>(new Map());

/** Atom storing the last error encountered when attempting to save demographics. */
export const demographicErrorAtom = atomWithReset<Error | null>(null);

/** Atom containing the set of demographic IDs currently being saved. */
export const savingDemographicIdsAtom = atomWithReset<Set<string>>(new Set<string>());

/**
 * Returns a derived atom that computes the most recent non-deleted demographic for a given field.
 *
 * @param fieldId - The ID of the field to find the demographic for.
 * @returns An atom representing the most recent non-deleted demographic, or null if no demographic is found.
 */
export const currentDemographicAtomFamily = atomFamily((fieldId: string) =>
  selectAtom(demographicMapAtom, (map: Map<string, Demographic[]>): Demographic | null =>
    map.get(fieldId)?.find(d => !d.deletedAt) ?? null
  )
);

/**
 * Returns a set of selected group IDs from the most recent (non-deleted) demographic reply for each field.
 */
export const selectedGroupIdsAtom = atom((get) => {
  const demographicMap: Map<string, Demographic[]> = get(demographicMapAtom);

  const selectedGroupIds = new Set<string>();
  for (const demographics of demographicMap.values()) {
    const mostRecent: Demographic | undefined = demographics.find(d => !d.deletedAt);
    if (!mostRecent) continue;

    for (const id of mostRecent.groups ?? []) selectedGroupIds.add(id);
  }
  return selectedGroupIds;
});

/**
 * Check whether any of the given skip groups are selected in the current demographics.
 *
 * @param element - The element to check for skip groups.
 * @returns An atom that evaluates to `true` if at least one of the element’s skip groups is present in the current
 *        demographic selections.
 */
export const skippedStatusAtomFamily = atomFamily((element: Element) =>
  atom((get) => {
    if (!hasSkipGroups(element)) return false;

    const skipGroupIds: string[] = (element as any).skipGroups;
    const selectedGroupIds: Set<string> = get(selectedGroupIdsAtom);
    return skipGroupIds.some((skipGroupId: string) =>
      selectedGroupIds.has(skipGroupId)
    );
  }
));

/**
 * Determines whether an element is an email address verification field that can be skipped.
 *
 * @param element - The survey element to check.
 * @returns An atom resolving to `true` if the element is an email address verification field that can be skipped.
 */
export const skippedEmailAddressVerificationAtomFamily = atomFamily((element: Element) =>
  atom((get) => {
    if (!('category' in element) || element.category !== 'EAV') return false;

    const user: User = get(userAtom);
    return user.isEmailAddressVerified || !user.isEmailAddressVerificationRequired;
  })
);

/**
 * Map of unsaved demographics keyed by demographic ID.
 * - Key: demographicId (string)
 * - Value: fieldId (string) for the demographic
 */
export const unsavedDemographicsAtom = atomWithReset<Map<string, string>>(new Map<string, string>());